// utils/appLifecycle.js
// 전역 라이프사이클/스토리지 유틸 (iOS 백그라운드 복귀 대응)

const KEY = {
  LAST_BG_TS: "__app:last_bg_ts",
  LAST_RELOAD_TS: "__app:last_reload_ts",
  LAST_HEARTBEAT: "__app:last_hb_ts",
};

const HARD_RELOAD_AFTER_MS = 60_000; // 60초 이상 백그라운드면 하드 리로드
const RELOAD_COOLDOWN_MS = 30_000;  // 30초 내 재리로드 금지
const WS_GUARD_DELAY_MS = 2000;    // 복귀 후 화이트스크린 감지 대기(기본값 상향: 2.0s)
const HEARTBEAT_INTERVAL_MS = 5_000; // 5초마다 하트비트
const STALE_HEARTBEAT_MS = 60_000; // 60초 넘게 하트비트 없으면 stale 판단

let hbTimer = null;

const isIOS = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

const now = () => Date.now();

const ssGet = (k, def = null) => {
  try {
    const v = sessionStorage.getItem(k);
    return v == null ? def : JSON.parse(v);
  } catch { return def; }
};
const ssSet = (k, v) => {
  try { sessionStorage.setItem(k, JSON.stringify(v)); } catch { }
};
const lsGet = (k, def = null) => {
  try {
    const v = localStorage.getItem(k);
    return v == null ? def : JSON.parse(v);
  } catch { return def; }
};
const lsSet = (k, v) => {
  try { localStorage.setItem(k, JSON.stringify(v)); } catch { }
};

const seconds = (ms) => Math.round(ms / 1000);

// ---------- heartbeat ----------
function startHeartbeat() {
  stopHeartbeat();
  // 즉시 1회 기록
  lsSet(KEY.LAST_HEARTBEAT, now());
  hbTimer = setInterval(() => {
    lsSet(KEY.LAST_HEARTBEAT, now());
  }, HEARTBEAT_INTERVAL_MS);
}
function stopHeartbeat() {
  if (hbTimer) {
    clearInterval(hbTimer);
    hbTimer = null;
  }
}
function isStaleHeartbeat() {
  const last = lsGet(KEY.LAST_HEARTBEAT, 0);
  if (!last) return true; // 기록이 없으면 stale로 본다
  return now() - last > STALE_HEARTBEAT_MS;
}

// ---------- bg/fg ----------
function markBackground() {
  ssSet(KEY.LAST_BG_TS, now());
  // 백그라운드에선 하트비트 중지
  stopHeartbeat();
}

function shouldHardReload() {
  if (!isIOS()) return false; // 안드로이드는 통상 필요 없음
  const lastBg = ssGet(KEY.LAST_BG_TS, 0);
  if (!lastBg) return false;
  const elapsed = now() - lastBg;
  return elapsed >= HARD_RELOAD_AFTER_MS;
}

// ---------- recover/reload ----------
function reloadWithCooldown(reason = "unknown") {
  const lastReload = ssGet(KEY.LAST_RELOAD_TS, 0);
  if (now() - lastReload < RELOAD_COOLDOWN_MS) {
    console.log("[Lifecycle] reload blocked by cooldown");
    return;
  }
  ssSet(KEY.LAST_RELOAD_TS, now());
  console.log(`[Lifecycle] HARD RELOAD: ${reason}`);
  window.location.reload();
}

function softRecover(reason = "soft-recover") {
  console.log(`[Lifecycle] SOFT RECOVER: ${reason}`);
  // 전역으로 소프트 복구 신호 방송 (페이지에서 필요 시 리스너 추가)
  window.dispatchEvent(new CustomEvent("APP_SOFT_RECOVER", { detail: { reason } }));
}

// 단계적 복구: 우선 소프트 → 그래도 이상하면 하드
async function stagedRecover(reason = "staged") {
  softRecover(reason);
  // rAF 두 틱 대기 후 다시 헬스체크하여 여전히 이상하면 하드 리로드
  await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
  const unhealthy = await isUnhealthy();
  if (unhealthy.ok === false) {
    reloadWithCooldown(`staged-hard:${unhealthy.reason}`);
  }
}

// ---------- health checks ----------
async function checkRafTicks(ticks = 2) {
  // rAF가 정상적으로 돈다면 렌더러가 살아있다고 간주
  for (let i = 0; i < ticks; i++) {
    await new Promise((r) => requestAnimationFrame(r));
  }
  return true;
}

function checkRootShape() {
  const root = document.getElementById("root");
  if (!root) return { ok: false, reason: "no-root" };
  const rect = root.getBoundingClientRect?.();
  const hasChildren = root.childElementCount > 0;
  const bigEnough = rect && rect.height >= 10 && rect.width >= 10;
  if (!hasChildren) return { ok: false, reason: "empty-root" };
  if (!bigEnough) return { ok: false, reason: "tiny-root" };
  return { ok: true };
}

function checkNetwork() {
  if (typeof navigator.onLine === "boolean" && !navigator.onLine) {
    return { ok: false, reason: "offline" };
  }
  return { ok: true };
}

function checkHeartbeatFresh() {
  if (isStaleHeartbeat()) return { ok: false, reason: "stale-heartbeat" };
  return { ok: true };
}

async function isUnhealthy() {
  const net = checkNetwork();
  if (!net.ok) return net;

  const root = checkRootShape();
  if (!root.ok) return root;

  // rAF 두 틱으로 페인트 동작 확인
  try {
    await checkRafTicks(2);
  } catch {
    return { ok: false, reason: "raf-failed" };
  }

  const hb = checkHeartbeatFresh();
  if (!hb.ok) return hb;

  return { ok: true };
}

// ---------- white screen guard ----------
function guardWhiteScreen(opts = {}) {
  const delay = Number(opts.delay ?? WS_GUARD_DELAY_MS);
  setTimeout(async () => {
    const unhealthy = await isUnhealthy();
    if (unhealthy.ok === false) {
      // 네트워크 오프라인이면 일단 소프트(재시도 기회) → 이후 쿨다운 내 재리로드 방지
      if (unhealthy.reason === "offline") {
        softRecover("offline");
      } else if (unhealthy.reason === "stale-heartbeat") {
        reloadWithCooldown("stale-heartbeat");
      } else {
        reloadWithCooldown(`white-screen:${unhealthy.reason}`);
      }
    }
  }, delay);
}

export const AppLifecycle = {
  KEY,
  markBackground,
  shouldHardReload,
  reloadWithCooldown,
  softRecover,
  stagedRecover,
  guardWhiteScreen,
  isIOS,
  seconds,
  // heartbeat
  startHeartbeat,
  stopHeartbeat,
  isStaleHeartbeat,
};
